import { useMemo, useState } from "react";
import {
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import { TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { getBudgetFieldsConfig } from "@/other/constants/TaskBudget/taskBudgetViewColumnsData";
import { isColorBudgetCell } from "@/other/utils/budgetUtils";
import { cn } from "@/modern/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";
import {
  COST_CATEGORIES,
  formatCompactCurrency,
  formatPercent,
  type BudgetViewProps,
} from "./budgetViewUtils";

/* ------------------------------------------------------------------ */
/*  Radial chart (fixed-size, matching demo)                          */
/* ------------------------------------------------------------------ */

const CHART_SIZE_PX = 90;

const RadialChart = ({
  pct,
  color,
}: {
  pct: number;
  color: string;
}) => {
  const clamped = Math.max(0, Math.min(100, pct));
  const chartData = [{ value: clamped, fill: color }];

  return (
    <div className="relative" style={{ width: CHART_SIZE_PX, height: CHART_SIZE_PX }}>
      <RadialBarChart
        width={CHART_SIZE_PX}
        height={CHART_SIZE_PX}
        cx={CHART_SIZE_PX / 2}
        cy={CHART_SIZE_PX / 2}
        innerRadius={CHART_SIZE_PX * 0.34}
        outerRadius={CHART_SIZE_PX * 0.48}
        barSize={6}
        data={chartData}
        startAngle={90}
        endAngle={-270}
      >
        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
        <RadialBar
          background={{ fill: "hsl(var(--secondary))" }}
          dataKey="value"
          cornerRadius={4}
          angleAxisId={0}
        />
      </RadialBarChart>
      <span className="absolute inset-0 flex items-center justify-center text-base font-medium text-foreground">
        {Math.round(clamped)}%
      </span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Variance badge                                                    */
/* ------------------------------------------------------------------ */

const VarianceBadge = ({ value, isPercent }: { value: number; isPercent?: boolean }) => {
  if (value === 0) return null;
  const positive = value > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium tabular-nums leading-none mt-1",
        positive
          ? "bg-green-500/15 text-green-600 dark:text-green-400"
          : "bg-red-500/15 text-red-600 dark:text-red-400"
      )}
    >
      {positive ? "+" : ""}
      {isPercent ? `${value}%` : formatCompactCurrency(value)}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Detail panel (user-preferred fields for sidebar)                  */
/* ------------------------------------------------------------------ */

const getDefaultHoverFields = () =>
  getBudgetFieldsConfig().slice(0, 6).map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (f: any) => f.budgetField as string
  );

function formatFieldValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig: any,
  value: unknown
): string {
  if (value === undefined || value === null) return "N/A";
  if (fieldConfig.budgetValueFormatter) {
    return fieldConfig.budgetValueFormatter.format(value);
  }
  if (fieldConfig.isPercentage) {
    return `${value}%`;
  }
  return String(value);
}

const DetailPanel = ({
  budget,
  userPreferences,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  budget: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userPreferences: any;
}) => {
  const fields = useMemo(() => {
    let hoverOrder = getDefaultHoverFields();
    if (userPreferences) {
      try {
        const raw = userPreferences.getSetting(
          PERSISTENCE_STATE_KEYS.budgetHoverOrder,
          null,
          true
        );
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            hoverOrder = parsed;
          }
        }
      } catch {
        // fall back to defaults
      }
    }

    return hoverOrder.reduce<
      { key: string; title: string; value: string; isColored: boolean; rawValue: number }[]
    >((acc, fieldKey) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const config = getBudgetFieldsConfig().find((f: any) => f.budgetField === fieldKey);
      if (config) {
        const rawValue = budget[config.budgetField];
        acc.push({
          key: config.budgetField,
          title: config.budgetFieldTitle,
          value: formatFieldValue(config, rawValue),
          isColored: isColorBudgetCell(config.budgetField),
          rawValue: rawValue ?? 0,
        });
      }
      return acc;
    }, []);
  }, [budget, userPreferences]);

  return (
    <div className="flex gap-1 @[32.5rem]/budget:flex-col @[32.5rem]/budget:gap-3">
      {fields.map((field) => (
        <Tooltip key={field.key}>
          <TooltipTrigger asChild>
            <div className="flex-1 min-w-0 cursor-default">
              <p className="text-xs uppercase leading-tight text-muted-foreground truncate @[32.5rem]/budget:tracking-wide @[32.5rem]/budget:overflow-visible @[32.5rem]/budget:text-clip @[32.5rem]/budget:whitespace-normal">
                {field.title}
              </p>
              <p
                className={cn(
                  "text-sm font-medium tabular-nums mt-0.5 truncate @[32.5rem]/budget:overflow-visible @[32.5rem]/budget:text-clip @[32.5rem]/budget:whitespace-normal",
                  field.isColored && field.rawValue !== 0
                    ? field.rawValue < 0
                      ? "text-destructive"
                      : "text-green-600 dark:text-green-500"
                    : "text-foreground"
                )}
              >
                {field.value}
              </p>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[12.5rem]">
            <p className="text-[0.625rem] text-muted-foreground uppercase tracking-wide">{field.title}</p>
            <p className="text-sm font-medium tabular-nums mt-0.5">{field.value}</p>
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main view (Design #5 - Sidebar Right)                             */
/* ------------------------------------------------------------------ */

export const BudgetRadialView = ({ budget, userPreferences }: BudgetViewProps) => {
  const [mode, setMode] = useState<"estimated" | "actual">("actual");
  const isEst = mode === "estimated";

  const totalCost = isEst
    ? (budget[TASK_BUDGET_FIELDS.totalCostEstimated] ?? 0)
    : (budget[TASK_BUDGET_FIELDS.totalCostActual] ?? 0);

  const grossProfit = isEst
    ? (budget[TASK_BUDGET_FIELDS.grossProfitEstimated] ?? 0)
    : (budget[TASK_BUDGET_FIELDS.grossProfitActual] ?? 0);

  const grossMargin = isEst
    ? (budget[TASK_BUDGET_FIELDS.grossMarginEstimated] ?? 0)
    : (budget[TASK_BUDGET_FIELDS.grossMarginActual] ?? 0);

  const contractAmount = budget[TASK_BUDGET_FIELDS.contractAmount] ?? 0;
  const changeOrders = budget[TASK_BUDGET_FIELDS.changeOrders] ?? 0;
  const totalContract = budget[TASK_BUDGET_FIELDS.totalContract] ?? 0;

  const daysEstimated = budget[TASK_BUDGET_FIELDS.daysEstimated] ?? 0;
  const daysActual = budget[TASK_BUDGET_FIELDS.daysActual] ?? 0;
  const daysVariance = budget[TASK_BUDGET_FIELDS.daysVariance] ?? 0;

  const totalCostVariance = budget[TASK_BUDGET_FIELDS.totalCostVariance] ?? 0;
  const grossProfitVariance = budget[TASK_BUDGET_FIELDS.grossProfitVariance] ?? 0;
  const grossMarginVariance = budget[TASK_BUDGET_FIELDS.grossMarginVariance] ?? 0;

  const rings = useMemo(() => {
    const safeTotalCost = totalCost || 1;
    return COST_CATEGORIES.map((cat) => {
      const value = budget[isEst ? cat.est : cat.act] ?? 0;
      const variance = budget[cat.var] ?? 0;
      const pct = Math.min(Math.round((value / safeTotalCost) * 100), 100);
      return {
        label: cat.label,
        color: cat.color,
        percent: pct,
        amount: formatCompactCurrency(value),
        variance,
      };
    });
  }, [budget, isEst, totalCost]);

  const daysPct = daysEstimated > 0 ? Math.min((daysActual / daysEstimated) * 100, 100) : 0;
  const daysUnder = daysVariance > 0;

  return (
    <div className="@container/budget relative">
    <div className="p-4 @[32.5rem]/budget:pb-4 flex flex-col @[32.5rem]/budget:flex-row gap-4">
      {/* ---- Main content ---- */}
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        {/* Contract hero cards */}
        <div className="flex flex-wrap gap-3">
          {[
            { label: "Contract", value: contractAmount },
            { label: "Change Orders", value: changeOrders },
            { label: "Total Contract", value: totalContract },
          ].map((item) => (
            <div
              key={item.label}
              className="flex-1 min-w-[6rem] rounded-lg bg-card border border-border p-3"
            >
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {item.label}
              </p>
              <p
                className={cn(
                  "text-xl font-medium mt-1 mb-0 tabular-nums",
                  item.value < 0 ? "text-destructive" : "text-foreground"
                )}
              >
                {formatCompactCurrency(item.value)}
              </p>
            </div>
          ))}
        </div>

        {/* Cost breakdown card */}
        <div className="rounded-lg bg-card border border-border p-4 flex-1 @container/breakdown">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Cost Breakdown
            </h3>
            <div className="inline-flex rounded-full border border-border bg-secondary p-0.5">
              <button
                type="button"
                onClick={() => setMode("estimated")}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-full transition-all",
                  mode === "estimated"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground"
                )}
              >
                Estimated
              </button>
              <button
                type="button"
                onClick={() => setMode("actual")}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-full transition-all",
                  mode === "actual"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground"
                )}
              >
                Actual
              </button>
            </div>
          </div>

          {/* Radial rings row */}
          <div className="grid grid-cols-2 @[22rem]/breakdown:grid-cols-3 @[32rem]/breakdown:grid-cols-5 gap-3 mb-4 justify-items-center">
            {rings.map((ring) => (
              <div key={ring.label} className="flex flex-col items-center gap-1">
                <RadialChart pct={ring.percent} color={ring.color} />
                <span className="text-sm text-muted-foreground font-medium">
                  {ring.label}
                </span>
                <span className="text-base font-medium text-foreground tabular-nums">
                  {ring.amount}
                </span>
                {!isEst && ring.variance !== 0 && (
                  <VarianceBadge value={ring.variance} />
                )}
              </div>
            ))}
          </div>

          {/* Summary sub-cards */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[5rem] rounded-lg bg-background p-3 border border-border overflow-hidden">
              <p className="text-xs uppercase text-muted-foreground truncate">Total Cost</p>
              <p className="text-base font-medium text-foreground tabular-nums mt-0.5 mb-0 truncate">
                {formatCompactCurrency(totalCost)}
              </p>
              {!isEst && <VarianceBadge value={totalCostVariance} />}
            </div>
            <div className="flex-1 min-w-[5rem] rounded-lg bg-background p-3 border border-border overflow-hidden">
              <p className="text-xs uppercase text-muted-foreground truncate">Gross Profit</p>
              <p
                className={cn(
                  "text-base font-medium tabular-nums mt-0.5 mb-0 truncate",
                  grossProfit < 0 ? "text-destructive" : "text-success"
                )}
              >
                {formatCompactCurrency(grossProfit)}
              </p>
              {!isEst && <VarianceBadge value={grossProfitVariance} />}
            </div>
            <div className="flex-1 min-w-[5rem] rounded-lg bg-background p-3 border border-border overflow-hidden">
              <p className="text-xs uppercase text-muted-foreground truncate">Margin</p>
              <p className="text-base font-medium text-foreground tabular-nums mt-0.5 mb-0 truncate">
                {formatPercent(grossMargin)}
              </p>
              {!isEst && <VarianceBadge value={grossMarginVariance} isPercent />}
            </div>
          </div>
        </div>

        {/* Days tracking card */}
        <div className="rounded-lg bg-card border border-border p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase font-medium text-muted-foreground">
              Days Tracking
            </p>
            <span className="text-xs text-muted-foreground tabular-nums">
              {daysActual} / {daysEstimated} days
            </span>
          </div>
          <div className="relative h-2.5 rounded-full bg-secondary overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                daysUnder ? "bg-success" : "bg-destructive"
              )}
              style={{ width: `${daysPct}%` }}
            />
          </div>
          {daysVariance !== 0 && (
            <p
              className={cn(
                "text-xs font-medium mt-1.5",
                daysUnder ? "text-success" : "text-destructive"
              )}
            >
              {Math.abs(daysVariance)} days {daysUnder ? "under" : "over"} estimate
            </p>
          )}
        </div>
      </div>

      {/* ---- Sidebar (wide only) ---- */}
      <div className="hidden @[32.5rem]/budget:flex w-44 shrink-0 rounded-xl bg-card border border-border p-4 flex-col gap-4 overflow-y-auto">
        <DetailPanel budget={budget} userPreferences={userPreferences} />
      </div>
    </div>

    {/* ---- Sticky footer (narrow only) ---- */}
    <div className="sticky bottom-0 z-10 w-full bg-card/95 backdrop-blur-sm border-t border-border py-2 px-4 @[32.5rem]/budget:hidden">
      <DetailPanel budget={budget} userPreferences={userPreferences} />
    </div>
    </div>
  );
};
